import React, { useEffect, useState, useCallback } from "react";
import { Search, X, Check, ImageOff, Lock, Folder, FolderOpen } from "lucide-react";
import api from "../../lib/api";
import { imgSrc, imgSrcSet } from "../../lib/imgSrc";

/**
 * <ImagePicker /> — reusable drawer that lets an admin pick an image from
 * the library. Only shows images marked is_public=true AND with
 * consent_status in {'not_required','granted'} — so picks are safe to
 * insert into a public-facing slot without triggering a consent violation.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <ImagePicker
 *      open={open}
 *      onClose={() => setOpen(false)}
 *      onPick={(img) => setForm({ ...form, image_url: img.url })}
 *      category="products"   // optional initial filter
 *   />
 */
const ImagePicker = ({ open, onClose, onPick, category: initialCategory = "" }) => {
    const [categories, setCategories] = useState([]);
    const [category, setCategory] = useState(initialCategory);
    const [folder, setFolder] = useState("");
    const [folders, setFolders] = useState([]);
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get("/admin/image-categories").then((r) => setCategories(r.data || []))
            .catch(() => setCategories([]));
    }, [open]);

    useEffect(() => {
        if (!open) { setFolders([]); return; }
        if (!category) { setFolders([]); return; }
        api.get("/admin/image-folders", { params: { category } })
            .then((r) => setFolders(r.data || []))
            .catch(() => setFolders([]));
        setFolder("");
    }, [open, category]);

    const load = useCallback(async () => {
        if (!open) return;
        setLoading(true);
        try {
            const params = {};
            if (category) params.category = category;
            if (folder) params.folder = folder;
            if (q.trim()) params.q = q.trim();
            const { data } = await api.get("/admin/image-library/picker", { params });
            setItems(data || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [open, category, folder, q]);

    useEffect(() => {
        if (!open) return;
        const t = setTimeout(load, 200);
        return () => clearTimeout(t);
    }, [open, load]);

    useEffect(() => { if (open) setCategory(initialCategory); }, [open, initialCategory]);

    if (!open) return null;

    const onPickInternal = (img) => {
        try { onPick?.(img); } finally { onClose?.(); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid="image-picker">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
            <div className="relative w-full max-w-4xl bg-[#FDFBF7] h-full overflow-y-auto shadow-2xl flex flex-col">
                <header className="sticky top-0 bg-[#FDFBF7]/95 backdrop-blur border-b border-black/10 px-6 py-4 flex items-center justify-between z-10">
                    <h2 className="font-heading text-xl text-[#1C252E]">Pick from library</h2>
                    <button onClick={onClose} aria-label="Close" data-testid="image-picker-close" className="p-2 rounded-full hover:bg-black/5">
                        <X className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                </header>

                <div className="px-6 pt-4 pb-3 space-y-3 border-b border-black/5">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" strokeWidth={1.5} />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search title, caption, or tags…"
                            data-testid="image-picker-search"
                            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] text-sm"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setCategory("")}
                            data-testid="image-picker-cat-all"
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${category === "" ? "bg-[#E53935] text-white border-[#E53935]" : "bg-white text-[#1C252E] border-black/10 hover:bg-[#F4F1EA]"}`}
                        >
                            All
                        </button>
                        {categories.filter((c) => c.is_active !== false).map((c) => (
                            <button
                                key={c.id}
                                onClick={() => setCategory(c.slug)}
                                data-testid={`image-picker-cat-${c.slug}`}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${category === c.slug ? "bg-[#E53935] text-white border-[#E53935]" : "bg-white text-[#1C252E] border-black/10 hover:bg-[#F4F1EA]"}`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                    {category && folders.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-1" data-testid="image-picker-folders">
                            <button
                                onClick={() => setFolder("")}
                                data-testid="image-picker-folder-all"
                                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${folder === "" ? "bg-[#1C252E] text-white border-[#1C252E]" : "bg-white border-black/10 text-[#4A5568]"}`}
                            >
                                <FolderOpen className="w-3 h-3" strokeWidth={2} /> All folders
                            </button>
                            {folders.map((f) => (
                                <button
                                    key={f.id}
                                    onClick={() => setFolder(f.path)}
                                    data-testid={`image-picker-folder-${f.path}`}
                                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${folder === f.path ? "bg-[#1C252E] text-white border-[#1C252E]" : "bg-white border-black/10 text-[#4A5568] hover:bg-[#F4F1EA]"}`}
                                >
                                    <Folder className="w-3 h-3" strokeWidth={2} /> {f.path}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex-1 p-6">
                    {loading ? (
                        <p className="text-sm text-[#4A5568]">Loading…</p>
                    ) : items.length === 0 ? (
                        <div className="text-center py-16">
                            <ImageOff className="w-10 h-10 mx-auto mb-3 text-[#4A5568]/40" strokeWidth={1.5} />
                            <p className="text-sm text-[#4A5568]" data-testid="image-picker-empty">
                                No safe-to-use images found. Upload one in the Library tab or relax the filters.
                            </p>
                            <p className="text-xs text-[#4A5568] mt-2">
                                <Lock className="w-3 h-3 inline-block mr-1" strokeWidth={1.5} />
                                Images requiring consent are hidden unless consent is granted.
                            </p>
                        </div>
                    ) : (
                        <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="image-picker-grid">
                            {items.map((img) => (
                                <li key={img.id}>
                                    <button
                                        type="button"
                                        onClick={() => onPickInternal(img)}
                                        data-testid={`image-picker-item-${img.id}`}
                                        className="group w-full text-left bg-white border border-black/10 rounded-xl overflow-hidden hover:border-[#E53935] hover:shadow-md transition-all"
                                    >
                                        <div className="w-full h-32 bg-[#F4F1EA] flex items-center justify-center overflow-hidden">
                                            {img.url ? (
                                                <img
                                                    src={imgSrc(img.thumbnail_url || img.url, 320, 70)}
                                                    srcSet={imgSrcSet(img.thumbnail_url || img.url, 320, 70)}
                                                    alt={img.title}
                                                    loading="lazy"
                                                    decoding="async"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : <ImageOff className="w-6 h-6 text-[#4A5568]/40" strokeWidth={1.5} />}
                                        </div>
                                        <div className="p-2.5">
                                            <p className="text-xs font-medium text-[#1C252E] line-clamp-1" title={img.title}>{img.title}</p>
                                            <p className="text-[10px] text-[#4A5568] mt-0.5 line-clamp-1">{img.caption || "—"}</p>
                                        </div>
                                        <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none top-2 right-2 bg-[#E53935] text-white rounded-full p-1">
                                            <Check className="w-3 h-3" strokeWidth={2} />
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImagePicker;
